import { useMemo } from 'react';

import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useTournament } from '@/hooks/useTournament';
import { KeysOf, Maybe, Optional, TeamPlayer } from '@/types';
import { PlayerClub, PlayerHistoric, TemplatePlayer } from '@/types/team';
import { PlayerMyTeamF5 } from '@/types/teamf5';
import {
  getShortNamePlayerPosition,
  pluralizeWord,
  resolvePlayerPrice,
} from '@/utils';
import {
  PLAYER_POSITION_BY_ID,
  PLAYER_POSITION_LABEL_BY_ABBREVIATION,
} from '@/utils/constants';

type PositionKey = Optional<
  KeysOf<typeof PLAYER_POSITION_LABEL_BY_ABBREVIATION>
>;

export const useGetPlayerPositionById = (positionId?: number) => {
  return useMemo(() => {
    const id = positionId as KeysOf<typeof PLAYER_POSITION_BY_ID> | undefined;
    const position = id ? PLAYER_POSITION_BY_ID[id] : undefined;
    return position ?? null;
  }, [positionId]);
};

export const useGetPlayerPositionByAbbreviation = (
  abbreviation?: Maybe<string>,
  usePluralize = true
): string => {
  const { isMobile } = useIsDesktop();

  return useMemo(() => {
    const defaultLabel = 'jugadores';

    if (isMobile) {
      const selectedPosition = abbreviation as PositionKey;

      if (selectedPosition) {
        const selectedPositionLabel = usePluralize
          ? pluralizeWord(
              PLAYER_POSITION_LABEL_BY_ABBREVIATION[selectedPosition]
            )
          : PLAYER_POSITION_LABEL_BY_ABBREVIATION[selectedPosition];
        return selectedPositionLabel ?? defaultLabel;
      }

      return defaultLabel;
    }

    return defaultLabel;
  }, [abbreviation, isMobile, usePluralize]);
};

export const usePlayerTemplateMap = () => {
  const { userInitialData } = useAuth();

  return useMemo(() => {
    const map = new Map<number | undefined, TemplatePlayer>();
    userInitialData?.plantilla?.jugadores.forEach((obj) => {
      return map.set(obj.id, obj);
    });

    return map;
  }, [userInitialData?.plantilla?.jugadores]);
};

export const usePlayerClub = () => {
  const playerTemplateMap = usePlayerTemplateMap();
  const { tournamentMap } = useTournament();
  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;
  const teamsMap = tournamentMap?.tournamentTeamsMap;

  const resolvePlayerClubData = (
    templatePlayer: TemplatePlayer
  ): PlayerClub => {
    const statusInTournament = tournamentStatusMap
      ? tournamentStatusMap.get(templatePlayer.e)
      : undefined;

    const club = teamsMap?.get(templatePlayer.c);

    return {
      orden: templatePlayer.p,
      capitan: false,
      titular: false,
      id: templatePlayer.id,
      posicion: {
        id: templatePlayer.p,
        nombreCorto: getShortNamePlayerPosition(templatePlayer.p),
      },
      nombreCompleto: `${templatePlayer.a}, ${templatePlayer.n}`,
      cotizacion: templatePlayer.cot,
      status: statusInTournament,
      puntosPromedio: templatePlayer.pm,
      partidosJugados: templatePlayer.pj ? templatePlayer.pj : 0,
      ...(club && {
        club: club.nombre,
      }),
    } satisfies PlayerClub;
  };

  const resolveTeamPlayerData = (
    players: (PlayerMyTeamF5 | TeamPlayer | PlayerHistoric)[] | undefined
  ) => {
    return players?.map((player, index) => {
      const ordenIndex = index - 1;
      const isTitular = ordenIndex >= 11;
      const templatePlayer = playerTemplateMap.get(player.id);

      if (templatePlayer) {
        return {
          ...resolvePlayerClubData(templatePlayer),
          orden: ordenIndex + 1,
          cotizacion: resolvePlayerPrice(templatePlayer.cot),
          titular: isTitular,
        };
      }

      return {
        orden: ordenIndex,
        titular: isTitular,
        capitan: player.capitan,
      };
    });
  };

  return {
    resolvePlayerClubData,
    resolveTeamPlayerData,
  };
};
